import { get, isBoolean, isNil } from "lodash";

/**
 * Normalize Yup / lodash paths so `children[0].ssn` and `children.0.ssn`
 * resolve to the same key used by FormField registration.
 */
export const normalizeFieldPath = (path) => {
  if (!path || typeof path !== "string") {
    return path;
  }
  return path.replace(/\[(\d+)\]/g, ".$1");
};

/**
 * Whether a value counts as empty for required-field validation.
 * Booleans are never empty (false is a deliberate choice).
 */
export const isEmptyFieldValue = (value) => {
  if (isBoolean(value)) {
    return false;
  }
  if (isNil(value)) {
    return true;
  }
  if (typeof value === "string" && value.trim() === "") {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
};

/**
 * Sort field paths by their DOM position (top-to-bottom, then left-to-right
 * for sibling columns). Paths without a mounted element sink to the end.
 */
export const orderPathsByDomPosition = (paths, getElement) => {
  return [...paths].sort((leftPath, rightPath) => {
    const leftElement = getElement(leftPath);
    const rightElement = getElement(rightPath);

    if (!leftElement && !rightElement) {
      return 0;
    }
    if (!leftElement) {
      return 1;
    }
    if (!rightElement) {
      return -1;
    }

    const position = leftElement.compareDocumentPosition(rightElement);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    }
    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }
    return 0;
  });
};

/**
 * First path in visual order that has an entry in the error map.
 */
export const pickFirstErrorPath = (errorMap, orderedPaths) => {
  return orderedPaths.find((path) => Boolean(errorMap[path])) || null;
};

/**
 * Build an error map for required registered fields whose value is empty.
 * Unregistered / non-required fields are skipped.
 */
export const collectRequiredErrors = (
  registeredFields,
  values,
  messages = {},
) => {
  const requiredMessage =
    messages.REQUIRED || "Ce champ est obligatoire. Veuillez le compléter";
  const errors = {};

  registeredFields.forEach((field) => {
    if (!field?.path || !field.required) {
      return;
    }
    const value = get(values, field.path);
    if (isEmptyFieldValue(value)) {
      errors[field.path] = requiredMessage;
    }
  });

  return errors;
};

/**
 * Merge Yup validation errors with required-field errors.
 * Required errors win when both exist for the same path so the message
 * stays consistent with the progress registry.
 */
export const mergeErrorMaps = (yupErrors = {}, requiredErrors = {}) => ({
  ...yupErrors,
  ...requiredErrors,
});
